/** Hosts that serve OpenStad embed scripts and are expected to support CORS for error reporting. */
const OPENSTAD_SCRIPT_HOSTS = new Set([
  'api.v2.openstadwoonstad.nl',
  'openstad-cdn.nl',
]);

export const shouldUseScriptCrossOrigin = (scriptUrl: string): boolean => {
  try {
    return OPENSTAD_SCRIPT_HOSTS.has(new URL(scriptUrl).hostname);
  } catch {
    return false;
  }
};

type ScriptErrorContext = {
  scriptSrc: string;
  capturedAt: number;
};

let lastScriptErrorContext: ScriptErrorContext | undefined;

const CONTEXT_TTL_MS = 5_000;

export const registerScriptErrorContextListener = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const onError = (event: Event) => {
    const target = event.target;

    if (!(target instanceof HTMLScriptElement) || !target.src) {
      return;
    }

    lastScriptErrorContext = {
      scriptSrc: target.src,
      capturedAt: Date.now(),
    };
  };

  window.addEventListener('error', onError, true);

  return () => window.removeEventListener('error', onError, true);
};

export const consumeScriptErrorContext = (): ScriptErrorContext | undefined => {
  if (!lastScriptErrorContext) {
    return undefined;
  }

  if (Date.now() - lastScriptErrorContext.capturedAt > CONTEXT_TTL_MS) {
    lastScriptErrorContext = undefined;
    return undefined;
  }

  const context = lastScriptErrorContext;
  lastScriptErrorContext = undefined;

  return context;
};
